import { useId } from 'react';
import { Box, InputLabel, Typography } from '@mui/material';

import BaseFieldContainer from '../common/baseFieldContainer';
import { buildInputLabelSx } from '../fCheckboxGroup/utils/general';
import {
  F_BOX_CONTAINER_BASE_SX,
  F_BOX_CONTAINER_FIELD_SX,
  F_BOX_CONTAINER_SUBTITLE_SX,
} from './constants';

import type { ElementType } from 'react';
import type { FBoxContainerProps } from './types';

function FBoxContainer<RootComponent extends ElementType = 'div'>({
  sx,
  title,
  subtitle,
  required,
  showRequiredIndicator,
  disabled,
  error = false,
  fullWidth = true,
  inputLabelProps,
  children,
  ...rest
}: FBoxContainerProps<RootComponent>) {
  const generatedId = useId();
  const { sx: inputLabelSx, ...restInputLabelProps } = inputLabelProps ?? {};
  const showFieldChrome =
    title != null ||
    subtitle != null ||
    required != null ||
    showRequiredIndicator != null ||
    disabled != null ||
    error;
  const labelId = `fbox-container-label-${generatedId}`;

  if (!showFieldChrome) {
    return (
      <Box
        sx={[F_BOX_CONTAINER_BASE_SX, ...(sx == null ? [] : Array.isArray(sx) ? sx : [sx])]}
        {...rest}
      >
        {children}
      </Box>
    );
  }

  return (
    <BaseFieldContainer
      required={required}
      showRequiredIndicator={showRequiredIndicator}
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      alwaysRenderContainer
    >
      {title != null ? (
        <InputLabel
          {...restInputLabelProps}
          id={labelId}
          error={error}
          disabled={disabled}
          required={
            (showRequiredIndicator ?? (Boolean(required) && !disabled)) ? false : required
          }
          shrink
          sx={buildInputLabelSx(inputLabelSx)}
        >
          {title}
        </InputLabel>
      ) : null}
      <Box
        aria-labelledby={title != null ? labelId : undefined}
        sx={[
          F_BOX_CONTAINER_BASE_SX,
          F_BOX_CONTAINER_FIELD_SX,
          ...(sx == null ? [] : Array.isArray(sx) ? sx : [sx]),
        ]}
        {...rest}
      >
        {subtitle != null ? (
          <Typography sx={F_BOX_CONTAINER_SUBTITLE_SX}>{subtitle}</Typography>
        ) : null}
        {children}
      </Box>
    </BaseFieldContainer>
  );
}

export default FBoxContainer;
